import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PLAN_DEFINITIONS, QUOTE_STATUSES } from '@wrapforce/shared/constants';
import { Plus } from 'lucide-react';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { useQuotes } from './api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';

export function QuotesPage() {
  const { t } = useTranslation('quotes');
  const { formatMoney, formatRelative } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;
  const { data } = useQuotes(workspaceId, { status, locationId });

  return (
    <div className="flex flex-col gap-4 p-6" data-tour="page-quotes-list">
      <div className="flex items-center gap-3 overflow-x-auto">
        <button
          onClick={() => setStatus(undefined)}
          className={cn(' px-3 py-1.5 text-xs font-semibold', !status ? 'bg-ink text-surface' : 'text-muted')}
        >
          {t('list.all')} {data?.counts ? `(${Object.values(data.counts).reduce((a, b) => a + b, 0)})` : ''}
        </button>
        {QUOTE_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={cn('whitespace-nowrap px-3 py-1.5 text-xs font-semibold', status === s ? 'bg-ink text-surface' : 'text-muted')}
          >
            {t(`status.quote.${s}`)} {data?.counts?.[s] ? `(${data.counts[s]})` : ''}
          </button>
        ))}
        <Button size="sm" className="ml-auto gap-1.5 whitespace-nowrap" onClick={() => navigate('/quotes/new')}>
          <Plus size={14} /> {t('list.newQuote')}
        </Button>
      </div>

      {/* Mobile: tappable card rows. Tables scroll poorly on phones, so the
          same rows render as a stacked card list below md. */}
      <div className="flex flex-col gap-2 md:hidden">
        {(data?.items ?? []).map((q) => (
          <Link
            key={q.id}
            to={`/quotes/${q.id}`}
            className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5 text-left"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="truncate text-[15px] font-semibold text-ink">{q.customerName}</span>
              <span className="shrink-0 bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">
                {q.status === 'deposit_paid' && q.depositCents <= 0 ? t('accepted') : t(`status.quote.${q.status}`)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted">
              <span className="font-mono">Q-{q.number}</span>
              {q.vehicleLabel ? <span className="truncate">· {q.vehicleLabel}</span> : null}
            </div>
            <div className="font-mono text-sm font-semibold text-ink">{formatMoney(q.totalCents)}</div>
            <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5">
              <span className="shrink-0 text-[11px] text-muted">{formatRelative(new Date(q.updatedAt))}</span>
            </div>
          </Link>
        ))}
        {(data?.items.length ?? 0) === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-10 text-center text-sm text-muted">{t('list.empty')}</div>
        ) : null}
      </div>

      <div className="hidden overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2.5">{t('list.table.quote')}</th>
              <th className="px-4 py-2.5">{t('list.table.customer')}</th>
              <th className="px-4 py-2.5">{t('list.table.vehicle')}</th>
              <th className="px-4 py-2.5">{t('list.table.total')}</th>
              <th className="px-4 py-2.5">{t('list.table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((q) => (
              <tr key={q.id} className="border-b border-border last:border-0 hover:bg-surface-2">
                <td className="px-4 py-3">
                  <Link to={`/quotes/${q.id}`} className="font-mono font-semibold text-ink hover:text-accent-ink">
                    Q-{q.number}
                  </Link>
                </td>
                <td className="px-4 py-3 text-ink">{q.customerName}</td>
                <td className="px-4 py-3 text-muted">{q.vehicleLabel}</td>
                <td className="px-4 py-3 font-mono">{formatMoney(q.totalCents)}</td>
                <td className="px-4 py-3">
                  <span className="bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">
                    {q.status === 'deposit_paid' && q.depositCents <= 0 ? t('accepted') : t(`status.quote.${q.status}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data?.items.length ?? 0) === 0 ? <div className="px-4 py-10 text-center text-sm text-muted">{t('list.empty')}</div> : null}
      </div>
    </div>
  );
}
